import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Copy,
  Server,
  Code,
  Database,
  Cloud,
  Users,
  Building,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Loader2,
  GitBranch
} from 'lucide-react';
import { assetsApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn, getCriticalityBadge, getAssetTypeConfig, getStatusConfig, formatDate } from '../../lib/utils';
import * as XLSX from 'xlsx';

const assetTypeIcons = {
  hardware: Server,
  software: Code,
  dados: Database,
  servico: Cloud,
  pessoas: Users,
  instalacao: Building,
};

export default function AssetsList() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    asset_type: '',
    criticality: '',
    status: '',
  });
  const [sorting, setSorting] = useState([]);
  const [deleteAsset, setDeleteAsset] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadAssets();
    loadStats();
  }, [filters]);

  const loadAssets = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.asset_type) params.asset_type = filters.asset_type;
      if (filters.criticality) params.criticality = filters.criticality;
      if (filters.status) params.status = filters.status;
      if (searchQuery) params.search = searchQuery;
      
      const response = await assetsApi.getAll(params);
      setAssets(response.data);
    } catch (error) {
      toast.error('Erro ao carregar ativos');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await assetsApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadAssets();
  };

  const handleDelete = async () => {
    if (!deleteAsset) return;
    try {
      await assetsApi.delete(deleteAsset.asset_id);
      toast.success('Ativo eliminado com sucesso');
      setDeleteAsset(null);
      loadAssets();
      loadStats();
    } catch (error) {
      toast.error('Erro ao eliminar ativo');
    }
  };

  const handleExportExcel = () => {
    const exportData = assets.map(asset => ({
      'ID': asset.asset_id,
      'Nome': asset.name,
      'Tipo': getAssetTypeConfig(asset.asset_type).label,
      'Descrição': asset.description || '',
      'Criticidade': getCriticalityBadge(asset.criticality).label,
      'Confidencialidade': asset.confidentiality,
      'Integridade': asset.integrity,
      'Disponibilidade': asset.availability,
      'Departamento': asset.department || '',
      'Localização Física': asset.physical_location || '',
      'IP': asset.ip_address || '',
      'Hostname': asset.hostname || '',
      'Fabricante': asset.vendor || '',
      'Modelo': asset.model || '',
      'Estado': getStatusConfig(asset.operational_status).label,
      'Data Criação': formatDate(asset.created_at),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ativos');
    XLSX.writeFile(wb, `inventario_ativos_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Exportação concluída');
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'asset_id',
      header: 'ID',
      cell: ({ row }) => (
        <span className="font-mono text-xs text-slate-500">{row.original.asset_id}</span>
      ),
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 -ml-2"
        >
          Nome
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const asset = row.original;
        const Icon = assetTypeIcons[asset.asset_type] || Server;
        const typeConfig = getAssetTypeConfig(asset.asset_type);
        return (
          <div className="flex items-center gap-3">
            <div className={cn('h-8 w-8 rounded flex items-center justify-center bg-slate-100', typeConfig.color)}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{asset.name}</p>
              <p className="text-xs text-slate-500">{typeConfig.label}</p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'criticality',
      header: 'Criticidade',
      cell: ({ row }) => {
        const config = getCriticalityBadge(row.original.criticality);
        return (
          <Badge variant="outline" className={cn('text-xs', config.color)}>
            {config.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'department',
      header: 'Departamento',
      cell: ({ row }) => (
        <span className="text-sm text-slate-600">{row.original.department || '-'}</span>
      ),
    },
    {
      accessorKey: 'operational_status',
      header: 'Estado',
      cell: ({ row }) => {
        const config = getStatusConfig(row.original.operational_status);
        return (
          <Badge variant="outline" className={cn('text-xs', config.color)}>
            {config.label}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const asset = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0" data-testid={`asset-actions-${asset.asset_id}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                <Eye className="mr-2 h-4 w-4" />
                Ver Detalhes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/assets/${asset.asset_id}/edit`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/assets/new?copy=${asset.asset_id}`)}>
                <Copy className="mr-2 h-4 w-4" />
                Duplicar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => setDeleteAsset(asset)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ], [navigate]);

  const table = useReactTable({
    data: assets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  });

  const headerContent = (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={() => navigate('/assets/dependencies')} data-testid="dependencies-btn">
        <GitBranch className="h-4 w-4 mr-2" />
        Mapa
      </Button>
      <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-assets-btn">
        <Download className="h-4 w-4 mr-2" />
        Exportar
      </Button>
      <Button variant="outline" size="sm" onClick={() => navigate('/assets/import')} data-testid="import-assets-btn">
        <Upload className="h-4 w-4 mr-2" />
        Importar
      </Button>
      <Button variant="outline" size="sm" onClick={() => navigate('/assets/itam')} data-testid="itam-hub-btn" className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100">
        <Upload className="h-4 w-4 mr-2" />
        ITAM / CNCS
      </Button>
      <Button size="sm" onClick={() => navigate('/assets/new')} data-testid="new-asset-btn">
        <Plus className="h-4 w-4 mr-2" />
        Novo Ativo
      </Button>
    </div>
  );

  return (
    <Layout title="Gestão de Ativos" headerContent={headerContent}>
      <div className="space-y-6" data-testid="assets-list-container">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="card-flat">
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </CardContent>
            </Card>
            <Card className="card-flat">
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-red-600">{stats.by_criticality?.critica || 0}</p>
                <p className="text-xs text-slate-500">Críticos</p>
              </CardContent>
            </Card>
            <Card className="card-flat">
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-orange-600">{stats.by_criticality?.alta || 0}</p>
                <p className="text-xs text-slate-500">Alta</p>
              </CardContent>
            </Card>
            <Card className="card-flat">
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-yellow-600">{stats.by_criticality?.media || 0}</p>
                <p className="text-xs text-slate-500">Média</p>
              </CardContent>
            </Card>
            <Card className="card-flat">
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-amber-600">{stats.without_owner || 0}</p>
                <p className="text-xs text-slate-500">Sem Dono</p>
              </CardContent>
            </Card>
            <Card className="card-flat">
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-slate-600">{stats.near_end_of_support || 0}</p>
                <p className="text-xs text-slate-500">Fim Suporte</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Pesquisar ativos..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-assets-input"
                  />
                </div>
              </form>
              <div className="flex gap-2">
                <Select
                  value={filters.asset_type}
                  onValueChange={(value) => setFilters({ ...filters, asset_type: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-[140px]" data-testid="filter-type">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Tipos</SelectItem>
                    <SelectItem value="hardware">Hardware</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="dados">Dados</SelectItem>
                    <SelectItem value="servico">Serviço</SelectItem>
                    <SelectItem value="pessoas">Pessoas</SelectItem>
                    <SelectItem value="instalacao">Instalação</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.criticality}
                  onValueChange={(value) => setFilters({ ...filters, criticality: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-[140px]" data-testid="filter-criticality">
                    <SelectValue placeholder="Criticidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="critica">Crítica</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="baixa">Baixa</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-[140px]" data-testid="filter-status">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="manutencao">Manutenção</SelectItem>
                    <SelectItem value="descontinuado">Descontinuado</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => navigate('/assets/dependencies')} data-testid="view-dependencies-btn">
                  <GitBranch className="h-4 w-4 mr-2" />
                  Dependências
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-flat">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : assets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Server className="w-12 h-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">Nenhum ativo encontrado</h3>
                <p className="text-sm text-slate-500 mb-4">
                  {searchQuery || filters.asset_type || filters.criticality || filters.status
                    ? 'Tente ajustar os filtros'
                    : 'Comece por adicionar o primeiro ativo'}
                </p>
                <Button onClick={() => navigate('/assets/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Ativo
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full table-dense" data-testid="assets-table">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} className="border-b border-slate-200">
                          {headerGroup.headers.map((header) => (
                            <th key={header.id} className="px-4 py-3 text-left text-sm font-medium text-slate-500 bg-slate-50">
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/assets/${row.original.asset_id}`)}
                          data-testid={`asset-row-${row.original.asset_id}`}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="px-4 py-3"
                              onClick={(e) => {
                                if (cell.column.id === 'actions') e.stopPropagation();
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Mostrando {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} a{' '}
                    {Math.min(
                      (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                      assets.length
                    )}{' '}
                    de {assets.length} ativos
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteAsset} onOpenChange={() => setDeleteAsset(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Ativo</AlertDialogTitle>
              <AlertDialogDescription>
                Tem a certeza que pretende eliminar o ativo "{deleteAsset?.name}"?
                Esta ação não pode ser revertida.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
